import { useState } from "react";
import {
  Badge,
  Button,
  Card,
  Fieldset,
  Group,
  Radio,
  SegmentedControl,
  Skeleton,
  Stack,
  Table,
  Text,
  Textarea,
  Title,
} from "@mantine/core";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import { useMutation, useQuery } from "@tanstack/react-query";
import { languageName } from "@mip-tg-bot-turntable/platform-api";
import {
  ADMIN_LIST_PAGE_SIZE,
  adminErrText,
  adminGet,
  adminSend,
  AdminApiError,
} from "../../api.ts";
import { AdminTable } from "../../components/AdminTable.tsx";
import { TablePagination } from "../../components/TablePagination.tsx";

type TargetType = "all" | "users";

type PushStatus = "pending" | "sent" | "failed";

interface PushOutboxRow {
  id: string;
  tgUserId: number;
  kind: string;
  status: PushStatus;
  attempts: number;
  lastError: string | null;
  createdAt: string;
  sentAt: string | null;
}

const STATUS_COLOR: Record<PushStatus, string> = {
  pending: "gray",
  sent: "green",
  failed: "red",
};

export function PushTab(props: { readonly botId: string; readonly languages: readonly string[] }) {
  const { botId } = props;
  // 多语言覆盖项 = 该 bot 启用语言去掉 en-IN（en-IN 用上方默认/兜底文案）。
  const overrideLangs = props.languages.filter((c) => c !== "en-IN");
  const [text, setText] = useState("");
  const [i18nText, setI18nText] = useState<Record<string, string>>({});
  const [statusFilter, setStatusFilter] = useState("all");
  const [targetType, setTargetType] = useState<TargetType>("all");
  // 逗号或换行分隔的 tg_user_id 名单（纯数字）
  const [userIdsRaw, setUserIdsRaw] = useState("");

  type PushReminderPayload = {
    text: string;
    texts?: Record<string, string>;
    target: { type: "all" } | { type: "users"; userIds: number[] };
  };

  const sendReminder = useMutation({
    mutationFn: (body: PushReminderPayload) =>
      adminSend<{ enqueued: number }>("POST", `/bots/${botId}/push-reminder`, body),
    onSuccess: (res) => {
      notifications.show({ color: "green", message: `已加入推送队列 ${res.enqueued} 条` });
      setText("");
      setI18nText({});
      setUserIdsRaw("");
      setTargetType("all");
    },
    onError: (e) =>
      notifications.show({ color: "red", message: adminErrText((e as AdminApiError).code) }),
  });

  const outboxQuery = useQuery({
    queryKey: ["bot", botId, "push-outbox", statusFilter],
    queryFn: () => {
      const statusParam = statusFilter === "all" ? "" : `&status=${statusFilter}`;
      return adminGet<{ items: PushOutboxRow[] }>(
        `/bots/${botId}/push-outbox?limit=${ADMIN_LIST_PAGE_SIZE}${statusParam}`,
      );
    },
    refetchInterval: 15_000,
  });

  const parseUserIds = (): number[] | null => {
    const parts = userIdsRaw
      .split(/[\n,]+/)
      .map((s) => s.trim())
      .filter((s) => s !== "");
    const ids: number[] = [];
    for (const p of parts) {
      const n = Number(p);
      if (!Number.isInteger(n) || n <= 0) return null;
      ids.push(n);
    }
    return ids;
  };

  const handleSend = () => {
    if (text.trim().length === 0) {
      notifications.show({ color: "orange", message: "默认文案不能为空" });
      return;
    }

    let parsedUserIds: number[] | null = null;
    if (targetType === "users") {
      parsedUserIds = parseUserIds();
      if (parsedUserIds === null || parsedUserIds.length === 0) {
        notifications.show({ color: "orange", message: "请输入有效的 Telegram 用户 ID（纯数字）" });
        return;
      }
      if (parsedUserIds.length > 10000) {
        notifications.show({ color: "orange", message: "指定用户名单最多 10000 条" });
        return;
      }
    }

    const texts: Record<string, string> = {};
    for (const [k, v] of Object.entries(i18nText)) {
      const t = v.trim();
      if (t !== "") texts[k] = t;
    }
    const langKeys = Object.keys(texts);
    const preview = text.slice(0, 80) + (text.length > 80 ? "..." : "");

    const targetLabel =
      targetType === "all"
        ? "将推送给全部活跃用户，操作不可撤销。"
        : `将推送给指定 ${parsedUserIds!.length} 个用户（后端过滤未封禁），操作不可撤销。`;

    const target: PushReminderPayload["target"] =
      targetType === "all" ? { type: "all" } : { type: "users", userIds: parsedUserIds! };

    modals.openConfirmModal({
      title: "确认发送",
      children: (
        <Stack gap="xs">
          <Text size="sm">{targetLabel}</Text>
          <Text size="sm" c="dimmed">
            {langKeys.length > 0
              ? `多语言：默认文案 + ${langKeys.join(" / ")} 覆盖，按用户语言投递`
              : "单语言：所有用户收到默认文案"}
          </Text>
          <Text size="sm" c="dimmed">
            默认文案预览：{preview}
          </Text>
        </Stack>
      ),
      labels: { confirm: "确认发送", cancel: "取消" },
      confirmProps: { color: "blue" },
      onConfirm: () =>
        sendReminder.mutate(langKeys.length > 0 ? { text, texts, target } : { text, target }),
    });
  };

  const outboxRows = outboxQuery.data?.items ?? [];

  return (
    <Stack gap="md">
      <Card shadow="sm" padding="lg" radius="md" withBorder>
        <Stack gap="md">
          <Group justify="space-between" mb="xs">
            <Text fw={600} size="lg">
              活动提醒
            </Text>
          </Group>
          <Text size="sm" c="dimmed">
            向该 Bot 用户私聊发送一条活动提醒。发送后进入推送队列，Bot 进程异步逐条投递（约 28
            条/秒）。支持多语言：按用户语言投递对应文案。
          </Text>
          <Radio.Group
            label="收件人范围"
            value={targetType}
            onChange={(v) => setTargetType(v as TargetType)}
          >
            <Group mt="xs" gap="lg">
              <Radio value="all" label="全体活跃用户" />
              <Radio value="users" label="指定用户" />
            </Group>
          </Radio.Group>
          {targetType === "users" && (
            <Textarea
              label="Telegram 用户 ID 名单"
              description="每行或逗号分隔，填入纯数字 tg_user_id（最多 10000 条）。后端自动过滤非本 Bot 或已封禁用户。"
              placeholder={"123456789\n987654321"}
              minRows={4}
              maxRows={12}
              value={userIdsRaw}
              onChange={(e) => setUserIdsRaw(e.currentTarget.value)}
              styles={{ input: { fontFamily: "monospace" } }}
            />
          )}
          <Textarea
            label="默认 / 兜底文案"
            description="未配语言覆盖、或下方该语言留空的用户都收到这条"
            placeholder="请输入提醒内容（最多 1000 字符）"
            minRows={4}
            maxLength={1000}
            value={text}
            onChange={(e) => setText(e.currentTarget.value)}
          />
          <Fieldset legend="多语言覆盖（可选，留空则该语言用户收到默认文案）">
            <Stack gap="sm">
              {overrideLangs.map((lang) => (
                <Textarea
                  key={lang}
                  label={languageName(lang)}
                  placeholder={`${languageName(lang)} 文案`}
                  minRows={2}
                  maxLength={1000}
                  value={i18nText[lang] ?? ""}
                  onChange={(e) => {
                    // 先同步取值：currentTarget 在事件结束后被 React 置空，
                    // 不能在延迟执行的函数式 updater 里再读。
                    const v = e.currentTarget.value;
                    setI18nText((prev) => ({ ...prev, [lang]: v }));
                  }}
                />
              ))}
            </Stack>
          </Fieldset>
          <Group justify="flex-end">
            <Text size="xs" c="dimmed">
              {text.length} / 1000
            </Text>
            <Button
              onClick={handleSend}
              loading={sendReminder.isPending}
              disabled={text.trim().length === 0}
            >
              发送活动提醒
            </Button>
          </Group>
        </Stack>
      </Card>

      <Card shadow="sm" padding="lg" radius="md" withBorder>
        <Stack gap="md">
          <Group justify="space-between">
            <Title order={5}>近期推送队列</Title>
            <SegmentedControl
              size="xs"
              value={statusFilter}
              onChange={setStatusFilter}
              data={[
                { label: "全部", value: "all" },
                { label: "待发", value: "pending" },
                { label: "成功", value: "sent" },
                { label: "失败", value: "failed" },
              ]}
            />
          </Group>
          {outboxQuery.isLoading ? (
            <Stack gap="xs">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} h={36} radius="sm" />
              ))}
            </Stack>
          ) : outboxRows.length === 0 ? (
            <Text c="dimmed" size="sm">
              暂无推送记录
            </Text>
          ) : (
            <>
              <AdminTable minWidth={500}>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>TG ID</Table.Th>
                    <Table.Th>类型</Table.Th>
                    <Table.Th>状态</Table.Th>
                    <Table.Th>尝试</Table.Th>
                    <Table.Th>错误</Table.Th>
                    <Table.Th>创建时间</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {outboxRows.map((r) => (
                    <Table.Tr key={r.id}>
                      <Table.Td>
                        <Text size="xs">{r.tgUserId}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="xs">{r.kind}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Badge size="xs" color={STATUS_COLOR[r.status]}>
                          {r.status}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Text size="xs">{r.attempts}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="xs" c="red">
                          {r.lastError ?? "-"}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="xs">{new Date(r.createdAt).toLocaleString()}</Text>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </AdminTable>
              <TablePagination
                page={1}
                pageSize={ADMIN_LIST_PAGE_SIZE}
                total={outboxRows.length}
                totalPages={1}
                onPageChange={() => undefined}
              />
              <Text size="xs" c="dimmed">
                接口单次最多返回 {ADMIN_LIST_PAGE_SIZE} 条近期记录
              </Text>
            </>
          )}
        </Stack>
      </Card>
    </Stack>
  );
}
